import { FunctionComponent, useEffect, useRef, useState } from 'react';
import styles from "./Campaigns.module.css";
import cn from 'classnames';
import {Image} from 'react-bootstrap';
import SidebarLayout from '../../../component/common/Layout/SidebarLayout/SidebarLayout';
import CustomButton, { ButtonType, ButtonFont, ButtonSize} from '../../../component/common/CustomButton/CustomButton';
import SearchInput from '../../../component/common/SearchInput/SearchInput';
import TextHeader from '../../../component/common/TextHeader/TextHeader';
import CampaignsTable from './component/CampaignsTable/CampaignsTable';
import { ASSESTS } from '../../../constant/assest';
import { Outlet } from "react-router-dom";
import ContentTabs from '../../../component/common/ContentTabs/ContentTabs';
import CampaignsForm, { campaignTypeOptions } from '../../../component/UI/CampaignsForm/CampaignsForm';
import CampaignCard from '../../../component/UI/CampaignCard/CampaignCard';
import DeleteModal from '../../../component/common/DeleteModal/DeleteModal';
import { AppDispatch, RootState } from '../../../redux/store';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCampaigns, deleteCampaign } from '../../../redux/slices/campaigns/actions';
import { CampaignType, CampaignTabs, ICampaignStatus } from '../../../constant/enums';
import { useDebounce } from '../../../hooks/useDebounce';
import {format} from 'date-fns';
import { ICampaignData } from '../../../redux/slices/campaigns/types';
import { usePagination } from '../../../hooks/usePagination';
import Pagination from '../../../component/UI/Pagination/Pagination';
import CampaignCardSkeleton from '../../../component/UI/CampaignCard/CampaignCardSkeleton';
import { clearMessage, clearError  } from '../../../redux/slices/campaigns/slice';
import { toast } from 'react-toastify';

type FormMode = 'create' | 'view' | 'edit' | 'clone';

const Campaigns: FunctionComponent = () => {
    const dispatch= useDispatch<AppDispatch>();
    const { campaignsList, campaignsListLoading, deleteCampaignMessage, deleteCampaignError }= useSelector((state: RootState)=> state.campaigns);
    const [activeContentTab, setActiveContentTab] = useState(CampaignTabs.CATCH);
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search.trim(), 500);
    const [formMode, setFormMode] = useState<FormMode | null>(null);
    const [selectedCampaign, setSelectedCampaign] = useState<ICampaignData | null>(null);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10); 
    const [triggerRefetch, setTriggerRefetch] = useState(0);

    useEffect(()=> {      
        const tab= campaignTypeOptions.find((type)=> type.label=== activeContentTab)?.value || CampaignType.CATCH;
        const params = {
            page,
            limit,
            tab,
        };
        dispatch(fetchCampaigns(params));
        window.scrollTo(0,0);
    },[dispatch, activeContentTab, page, limit, triggerRefetch])
    
    const formRef = useRef<HTMLDivElement | null>(null);
    const scrollToForm = () => {
        if (formRef.current) {
        formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };

    const handleAddNewCampaign = () => {
        setSelectedCampaign(null);
        setFormMode('create');
        scrollToForm();
    };

    const handleViewCampaign = (campaign: ICampaignData) => {
        setSelectedCampaign(campaign);
        setFormMode('view');
        scrollToForm();
    };

    const handleEditCampaign = (campaign: ICampaignData) => {
        setSelectedCampaign(campaign);
        setFormMode('edit');
        scrollToForm();
    };

    const handleCloneCampaign = (campaign: ICampaignData) => {
        setSelectedCampaign({ ...campaign, name: `${campaign.name}_clone` });
        setFormMode('clone');
        scrollToForm();
    };

    const handleDeleteCampaign = (campaign: ICampaignData) => {
        setSelectedCampaign(campaign);
        setShowDeleteModal(true);
    };

    const handleCloseForm = () => {
        setFormMode(null);
        setSelectedCampaign(null);
    }

    const handleSuccess = () => {
        const currentMode = formMode;
        handleCloseForm();
        if ((currentMode === 'create' || currentMode === 'clone') && page !== 1) {
            setPage(1);
        } else {
            setTriggerRefetch(prev => prev + 1);
        }
    };
    
    const confirmDelete = () => {
        if (selectedCampaign) {
            dispatch(deleteCampaign(selectedCampaign.id)).unwrap().then(()=> {
                setShowDeleteModal(false);
                setSelectedCampaign(null);
                setTriggerRefetch(prev => prev + 1);
            })
            .catch((err)=> console.error('delete failed:', err));
        }
    };
    useEffect(()=> {
        if(deleteCampaignMessage){
            toast.success(deleteCampaignMessage);
            dispatch(clearMessage());
        }
        if(deleteCampaignError){
            toast.error(deleteCampaignError);
            dispatch(clearError());
        }
    }, [dispatch, deleteCampaignMessage, deleteCampaignError])

    const paginationGroup = usePagination({
        currentPageIndex: page - 1,
        totalPages: campaignsList.totalPages,
    });

    const pageNumbersElements = paginationGroup.map((p, index) => {
        if (typeof p === 'string') {
            return <span key={`ellipsis-${index}`} className={cn(styles.pageNumberBtn)} style={{cursor: 'default'}}>{p}</span>;
        }
    
        const pageIndex = p;
        return (
            <CustomButton
            key={pageIndex}
            onClick={() => setPage(pageIndex + 1)}
            disabled={page === pageIndex + 1}
            btnType={
                page === pageIndex + 1
                ? ButtonType.ACTIVENOSTYLE
                : ButtonType.NOSTYLE
            }
            btnSize={ButtonSize.NORMAL}
            btnFont={
                page === pageIndex + 1
                ? ButtonFont.BOLD
                : ButtonFont.NORMAL
            }
            className={cn(
                styles.pageNumberBtn,
                page === pageIndex + 1 && styles.activePageNumberBtn
            )}
            >
            {pageIndex + 1}
            </CustomButton>
        );
    });

    return (
        <SidebarLayout>
            <div className={cn(styles.campaignsTopRow)}>
                <TextHeader 
                    title={'Anglers Engagement'} 
                    summary={"Monitor and manage your referral program performance"}
                    className='mb-4'
                />
                <ContentTabs tabs={campaignTypeOptions.map((type)=> type.label)} 
                    defaultActiveTab={activeContentTab} 
                    className='mb-4'
                    tabOnClick={(tab) => {
                        setActiveContentTab(tab as CampaignTabs);
                        setPage(1); // Reset page on tab change
                    }} 
                />
                <div className={styles.anglersEngagementHeader}>
                    <div className={styles.title}>
                        <span className={styles.header}>Campaign ({activeContentTab})</span> Select an existing campaign or create a new one
                    </div>
                    <CustomButton 
                        className={styles.addNewContentBtn}
                        btnType={ButtonType.GREEN}
                        onClick={handleAddNewCampaign}
                    >
                        <Image src={ASSESTS.ADD} alt={'add'} /> Add New Campaign
                    </CustomButton>
                </div>
                <div className={styles.campaignsCardsRow}>
                    {campaignsListLoading === 'pending' && (                  
                      Array.from({length: 4}).map((_, index)=> (
                        <CampaignCardSkeleton key={index} className={styles.campaignsOverViewCard} />
                    )))}
                    {campaignsListLoading === 'failed' && (
                        <p className={styles.errorMessage}>Something went wrong!</p>
                    )}
                    {campaignsListLoading === 'succeeded' && campaignsList.trasformData.length=== 0 && (
                        <p className={styles.noResultsMessage}>No results found</p>
                    )}
                    {campaignsListLoading === 'succeeded' && campaignsList.trasformData.length > 0 &&
                    campaignsList.trasformData.map((item, index) => (
                        <CampaignCard 
                            key={`${item.name}-${index}`}
                            className={styles.campaignsOverViewCard}
                            icon={<Image src={ASSESTS.FISH} alt='Fish' width={24} height={22} />}
                            title={item.name}
                            status={item.status as ICampaignStatus}
                            tag={'Engagement'}
                            date={format(new Date(item.start_date), ('MMM d, yyyy'))}
                            user={'All Users'}
                            onView={() => handleViewCampaign(item)}
                            onEdit={() => handleEditCampaign(item)}
                            onClone={() => handleCloneCampaign(item)}
                            onDelete={() => handleDeleteCampaign(item)}
                        />
                    ))
                }
                </div>
                <Pagination
                    enteriesShows={`${campaignsList.total
                            ? (page - 1) * limit + 1
                            : 0
                        }-${Math.min(
                            (page) * limit,
                            campaignsList.total || 0
                        )} of ${campaignsList.total || 0} Entries`}
    
                    pageNumbers= {[
                        campaignsList.totalPages > 1 && (
                            <CustomButton
                                key="prev"
                                onClick={() => setPage(page - 1)}
                                disabled={page <=1}
                                btnType={ButtonType.NOSTYLE}
                                className={styles.pageNumberBtn}
                            >
                                Prev
                            </CustomButton>
                        ),
                        ...pageNumbersElements,
                        campaignsList.totalPages > 1 && (
                            <CustomButton
                                key="next"
                                onClick={() => setPage(page + 1)}
                                disabled={page >= campaignsList.totalPages}
                                btnType={ButtonType.NOSTYLE}
                                className={styles.pageNumberBtn}
                            >
                                Next
                            </CustomButton>
                        )
                        ].filter(Boolean)}
                    dropDownOptions={[
                        // { value: "2", label: "2" },
                        { value: "5", label: "5" },
                        { value: "10", label: "10" },
                        { value: "20", label: "20" },
                        { value: "25", label: "25" },
                    ]}
                    dropdownOnChange={(value) => {
                        setLimit(Number(value));
                        setPage(1);
                    }}
                    dropDownPlaceholder={limit.toString()}
                />
            </div>
            <div ref={formRef} className={styles.campaignsFormRow}>
                {formMode && <CampaignsForm
                    mode={formMode}
                    campaign={selectedCampaign}
                    onCancel={handleCloseForm}
                    onSuccess={handleSuccess}
                />}
            </div>
            <div className={cn(styles.campaignsBottomRow)}>
                <TextHeader 
                    title={'Fishinity Accounts'} 
                    className='mb-3'
                />
                <div className={styles.tableFilter}>
                    <SearchInput placeholder='Search subscriptions...' className={styles.proAccountsSearch} value= {search} onChange={(e) => setSearch(e.target.value)} />
                </div>
                <CampaignsTable
                    activeTab={activeContentTab}
                    search={debouncedSearch}
                />
            </div>
            <DeleteModal 
                title={'Delete Compaing'}
                showModal={showDeleteModal}
                onClose={() => setShowDeleteModal(false)} 
                bodyText={'Are you sure want to delete this Item?'} 
                onDelete={confirmDelete}
            />
            <Outlet />
        </SidebarLayout>
    );
};

export default Campaigns;